import { authStateAtom } from '../models/atoms/auth_atoms';
import { setRecoil } from 'recoil-nexus';
import { tokenResponseInterface } from '../models/interfaces/auth_interfaces';
import { firstTimeLoginAtom } from '../models/atoms/account_atoms';
import {
  AccountInfo,
  InteractionRequiredAuthError,
  InteractionStatus,
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import {
  debouncedInternalServerErrorAlert,
  debouncedOfflineAlert,
  debouncedSubdomainAlert,
} from '../utils/debouncedAlerts';

const auth_link = import.meta.env.REACT_APP_AUTH_API_URL;

export const msalConfig = {
  auth: {
    clientId: import.meta.env.REACT_APP_MSAL_CLIENT_ID || '',
    authority: import.meta.env.REACT_APP_MSAL_AUTHORITY || '',
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

const swap_microsoft_token = async (accessToken: string, idToken: string) => {
  const link = auth_link + '/msal_token';
  const object: RequestInit = {
    method: 'POST',
    headers: {
      msal_token: accessToken,
      id_token: idToken,
    },
  };
  try {
    const response = await fetch(link, object);
    if (!response.ok) {
      console.error(response.statusText);
      const error = await response.json();
      if (error.detail === 'Company not found') {
        alert(
          'Your organization has not been enrolled for Microsoft SSO. Please log in with your username and password.',
        );
      } else {
        alert('Failed to login with Microsoft SSO. Error: ' + error.detail);
      }
      return;
    }
    const token = await response.json();
    setRecoil(authStateAtom, {
      token: token?.access_token ?? '',
      validToken: true,
      showInvalidCredWarning: false,
    });
    localStorage.setItem('token', token?.access_token ?? '');
    localStorage.setItem('firstTimeLogin', token.first_login.toString());
    setRecoil(firstTimeLoginAtom, token.first_login);
  } catch (error) {
    console.error(error);
    alert(error);
  }
};

const microsoft_get_access_token = async (
  instance: IPublicClientApplication,
  accounts: AccountInfo[],
): Promise<{ accessToken: string; idToken: string }> => {
  const accessTokenRequest = {
    scopes: ['user.read'],
    account: accounts[0],
  };
  let accessToken;
  let idToken;
  try {
    const response = await instance.acquireTokenSilent(accessTokenRequest);
    accessToken = response.accessToken;
    idToken = response.idToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const response = await instance.acquireTokenPopup(accessTokenRequest);
        accessToken = response.accessToken;
        idToken = response.idToken;
      } catch (error) {
        console.error(error);
        alert('Microsoft SSO login failed. Please try again.');
        throw error;
      }
    } else {
      alert(`Unexpected error while authenticating: ${error}`);
      throw error;
    }
  }
  return { accessToken, idToken };
};

export const auth_controller = {
  msalInstance,
  get_auth_key: async function (username: string, password: string) {
    const link = auth_link + '/token';
    const object: RequestInit = {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ username, password }),
    };
    try {
      const response = await fetch(link, object);
      if (response.status >= 500) {
        debouncedInternalServerErrorAlert();
        return;
      }

      const json = await response.json();

      if (!response.ok) {
        alert(
          typeof json.detail === 'string' ? json.detail : 'Invalid Credentials',
        );
        setRecoil(authStateAtom, {
          token: '',
          validToken: false,
          showInvalidCredWarning: true,
        });
        return;
      }

      const tokenResponse = json as tokenResponseInterface;
      if (tokenResponse.first_login) {
        setRecoil(firstTimeLoginAtom, tokenResponse.first_login);
      }
      localStorage.setItem(
        'firstTimeLogin',
        tokenResponse.first_login.toString(),
      );
      setRecoil(authStateAtom, {
        token: tokenResponse?.access_token ?? '',
        validToken: true,
        showInvalidCredWarning: false,
      });
      localStorage.setItem('token', tokenResponse?.access_token ?? '');
    } catch (e) {
      console.error(e);

      if (!navigator.onLine) {
        debouncedOfflineAlert();
        return;
      }

      if (e instanceof TypeError) {
        debouncedSubdomainAlert();
      }
    }
  },

  microsoft_login: async function (
    instance: IPublicClientApplication,
    accounts: AccountInfo[],
    inProgress: InteractionStatus,
  ): Promise<boolean> {
    if (inProgress !== InteractionStatus.None) {
      return false;
    }
    if (accounts.length === 0) {
      instance.loginPopup();
      return false;
    }
    const { accessToken, idToken } = await microsoft_get_access_token(
      instance,
      accounts,
    );
    await swap_microsoft_token(accessToken, idToken);
    return true;
  },

  check_token: function () {
    const token = localStorage.getItem('token');
    const obj = {
      link: auth_link + '/validate_token',
      object: {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      },
    };
    fetch(obj.link, obj.object).then(response => {
      if (response.ok) {
        setRecoil(authStateAtom, {
          token: token ?? '',
          validToken: true,
          showInvalidCredWarning: false,
        });
        setRecoil(
          firstTimeLoginAtom,
          localStorage.getItem('firstTimeLogin') === 'true',
        );
      }
    });
  },

  logout: function () {
    sessionStorage.clear();
    localStorage.setItem('token', '');
    setRecoil(authStateAtom, {
      token: '',
      validToken: false,
      showInvalidCredWarning: false,
    });
  },
};
